import { useMutation } from '@apollo/client';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Field } from '~/components/shared/Field';
import { UPDATE_BUSINESS_RULES } from '~/services/onboarding';
import { StepComponentProps } from '../types';

export const UpdateBusinessRules = ({
  business,
  moveNextStep,
  submitForm,
  setSubmitForm,
  setDisable,
}: StepComponentProps) => {
  const { t } = useTranslation('onboarding');
  const [updateBusinessRules] = useMutation(UPDATE_BUSINESS_RULES, {
    onCompleted: (data) => {
      if (data.response.hasError) {
        console.error(data.response.message);
        setSubmitForm(false);
      } else {
        moveNextStep(data.response.data);
      }
    },
    onError: (error) => {
      console.error(error);
      setSubmitForm(false);
    },
  });

  const { register, getValues, watch, setValue } = useForm({
    defaultValues: {
      business_id: business?._id || '',
      business_hours: business?.business_rules?.business_hours || '',
      customer_journey_description:
        business?.business_rules?.customer_journey_description || '',
      what_your_business_does:
        business?.business_rules?.what_your_business_does || '',
      greeting_example: business?.business_rules?.greeting_example || '',
    },
  });

  useEffect(() => {
    if (submitForm) {
      const data = getValues();
      updateBusinessRules({
        variables: {
          input: {
            ...data,
          },
        },
      });
    }
  }, [submitForm]);

  useEffect(() => {
    if (business.business_name && business.agent_name && business.agent_type) {
      setDisable(false);
    } else {
      setDisable(true);
    }
  }, []);

  const watchedFields = watch([
    'what_your_business_does',
    'business_hours',
    'customer_journey_description',
    'greeting_example',
  ]);

  useEffect(() => {
    const allFilled = watchedFields.every(
      (field) => field && field.trim() !== '',
    );
    setDisable(!allFilled);
  }, [watchedFields]);

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-3xl font-bold">{t('business_rules.title')}</h1>
      <p className="my-4 text-sm">{t('business_rules.description')}</p>
      <Field
        useSuggestions
        watch={watch}
        setValue={setValue}
        register={register}
        enablePulseAnimation={true}
        label={t('business_rules.what_your_business_does')}
        fieldName="what_your_business_does"
        type="textarea"
        placeholder={t('business_rules.what_your_business_does_placeholder')}
      />
      <Field
        register={register}
        label={t('business_rules.business_hours')}
        fieldName="business_hours"
        placeholder={t('business_rules.business_hours_placeholder')}
      />
      <Field
        register={register}
        useSuggestions
        watch={watch}
        setValue={setValue}
        label={t('business_rules.customer_journey_description')}
        fieldName="customer_journey_description"
        type="textarea"
        placeholder={t(
          'business_rules.customer_journey_description_placeholder',
        )}
      />
      <Field
        register={register}
        label={t('business_rules.greeting_example')}
        fieldName="greeting_example"
        placeholder={t('business_rules.greeting_example_placeholder')}
      />
    </div>
  );
};
